import type { CombinedFilter, FilterSpec, FilterType } from '../types/compute';

/** A fresh filter of a given type, preserving the id. Used when adding a filter
 *  or switching a filter's type (resets the type-specific fields). */
export function freshFilter(id: string, type: FilterType): FilterSpec {
  switch (type) {
    case 'spatial':
      return { id, type, solids: [] };
    case 'combined':
      return { id, type, expression: '' };
    case 'categorical':
      return { id, type, category_column: 0, category_value: '' };
    case 'continuous':
      return { id, type, continuous_column: 0 };
    case 'true':
    case 'false':
      return { id, type };
  }
}

/**
 * A categorical filter whose value LOOKS numeric (e.g. "1.0") can't run as
 * `categorical`: the reader stores numeric-looking cells as double and the C++
 * categorical filter any_casts the cell to string, throwing `bad any_cast`
 * (and even a tolerant stringification wouldn't reliably match "1.0" against a
 * double's rendering). Lower it to a continuous filter pinned to [n, n] — same
 * id, so combined expressions keep resolving. Non-numeric values (e.g. "oxido")
 * keep the string categorical filter; other filter types pass through.
 */
export function numericCategoricalToContinuous(f: FilterSpec): FilterSpec {
  if (f.type !== 'categorical') return f;
  const n = Number(f.category_value);
  if (f.category_value.trim() === '' || !Number.isFinite(n)) return f;
  return {
    id: f.id,
    type: 'continuous',
    continuous_column: f.category_column,
    min_limit_value: n,
    max_limit_value: n,
  };
}

// A combined expression that's a bare chain of ANDs (no OR/NOT/parentheses):
// while it looks like this, it's still the auto-generated default and we keep it
// in sync; once the user reaches for anything else, it's theirs and we back off.
const PLAIN_AND = /^\s*[A-Za-z0-9_]+(\s+AND\s+[A-Za-z0-9_]+)*\s*$/;

/** The next unused model-scoped filter id (`m{modelIndex}f{n}`) for a list. */
export function nextFilterId(existing: FilterSpec[], modelIndex: number): string {
  const ids = new Set(existing.map((f) => f.id));
  let n = 1;
  while (ids.has(`m${modelIndex}f${n}`)) n++;
  return `m${modelIndex}f${n}`;
}

// The "periods of interest" selector (US 2203) writes its own leaves and its
// own OR-combined into the same per-model filters[] withAutoCombined manages,
// under a reserved id convention (`m{modelIndex}period{n}` / `…periodOr`) so
// they never collide with the general `m{modelIndex}f{n}` cards a user adds
// by hand in FilterEditor.
export function periodOrFilterId(modelIndex: number): string {
  return `m${modelIndex}periodOr`;
}

/** Whether `id` is one of this model's auto-generated period leaf cards. */
export function isPeriodLeafId(id: string, modelIndex: number): boolean {
  return new RegExp(`^m${modelIndex}period\\d+$`).test(id);
}

/** The next unused model-scoped period-leaf id (`m{modelIndex}period{n}`). */
export function nextPeriodLeafId(existing: FilterSpec[], modelIndex: number): string {
  const ids = new Set(existing.map((f) => f.id));
  let n = 1;
  while (ids.has(`m${modelIndex}period${n}`)) n++;
  return `m${modelIndex}period${n}`;
}

/**
 * Keep a model's auto-maintained `combined` selector in step with its leaf
 * filters. A combined's expression is the per-model selector (see
 * batchRequestBuilder); rather than make the user hand-write "f1 AND f2", we
 * pre-fill and track it while it stays a plain AND-chain, then leave it alone
 * once the user reaches for OR/NOT/parentheses:
 *   • <2 leaves        → drop a still-pristine combined (a lone filter needs none)
 *   • ≥2, no combined  → append one ANDing the leaves
 *   • ≥2, pristine AND → refresh its expression to the current leaves
 *   • ≥2, customized   → untouched
 * Returned leaves-first, combined-last. Call it on add/remove of cards and on a
 * leaf id change — never on an edit of the combined's own expression.
 *
 * The period selector's leaves (`isPeriodLeafId`) are folded in as a single AND
 * operand rather than each counting separately: they collapse into their own
 * `periodOr` combined (`p1 OR p2 OR …`, fully derived — there is no UI path to
 * hand-edit it, so it is always regenerated) when there are ≥2, or stand in
 * directly when there is exactly 1. That operand then ANDs with the model's
 * other leaves exactly like any other card. Below-2/no-period-involvement is
 * the pre-existing fast path, kept byte-identical for every caller that never
 * touches periods.
 */
export function withAutoCombined(filters: FilterSpec[], modelIndex: number): FilterSpec[] {
  const orId = periodOrFilterId(modelIndex);
  const periodLeaves = filters.filter((f) => isPeriodLeafId(f.id, modelIndex));
  if (periodLeaves.length === 0 && !filters.some((f) => f.id === orId)) {
    const leaves = filters.filter((f) => f.type !== 'combined');
    const combined = filters.find((f): f is CombinedFilter => f.type === 'combined');
    if (leaves.length < 2) {
      return combined && PLAIN_AND.test(combined.expression) ? leaves : filters;
    }
    const expression = leaves.map((l) => l.id).join(' AND ');
    if (!combined) {
      const fresh: CombinedFilter = {
        id: nextFilterId(leaves, modelIndex),
        type: 'combined',
        expression,
      };
      return [...leaves, fresh];
    }
    if (PLAIN_AND.test(combined.expression)) {
      return [...leaves, { ...combined, expression }];
    }
    return filters;
  }

  const otherLeaves = filters.filter(
    (f) => f.type !== 'combined' && !isPeriodLeafId(f.id, modelIndex)
  );
  const topCombined = filters.find(
    (f): f is CombinedFilter => f.type === 'combined' && f.id !== orId
  );
  const periodOr: CombinedFilter | undefined =
    periodLeaves.length >= 2
      ? { id: orId, type: 'combined', expression: periodLeaves.map((l) => l.id).join(' OR ') }
      : undefined;
  const periodOperandId = periodOr
    ? periodOr.id
    : periodLeaves.length === 1
      ? periodLeaves[0].id
      : undefined;

  const base = [...otherLeaves, ...periodLeaves, ...(periodOr ? [periodOr] : [])];
  const leafIds = [...otherLeaves.map((l) => l.id), ...(periodOperandId ? [periodOperandId] : [])];

  if (!topCombined) {
    if (leafIds.length < 2) return base;
    const fresh: CombinedFilter = {
      id: nextFilterId([...otherLeaves, ...periodLeaves], modelIndex),
      type: 'combined',
      expression: leafIds.join(' AND '),
    };
    return [...base, fresh];
  }
  if (PLAIN_AND.test(topCombined.expression)) {
    if (leafIds.length < 2) return base;
    return [...base, { ...topCombined, expression: leafIds.join(' AND ') }];
  }
  // Customized (OR/NOT/parens): keep the user's own boolean logic verbatim —
  // but decision §1 ("periods AND with whatever else the user adds by hand")
  // applies here too, and periods have no hand-edit surface of their own to
  // keep themselves referenced. So make sure the CURRENT period operand is
  // reachable from the expression:
  //   • already referenced (the user wrote it, or a prior call injected it)
  //     → leave it exactly alone.
  //   • a STALE period-related id is referenced instead (the operand grew
  //     from a lone leaf into the OR-group, or shrank back) → repoint that
  //     token onto the current operand id, which stays stable either way.
  //   • not referenced at all → append it, parenthesizing the user's
  //     expression first so a bare append can't silently change its meaning
  //     under AND/OR precedence (`m0f1 OR m0f2 AND m0periodOr` reads as
  //     `m0f1 OR (m0f2 AND m0periodOr)`, not what decision §1 asks for).
  if (periodOperandId === undefined) return [...base, topCombined];
  const staleIds = [...periodLeaves.map((l) => l.id), orId].filter((id) => id !== periodOperandId);
  const remap = new Map(staleIds.map((id) => [id, periodOperandId]));
  let expression = remapExpressionIds(topCombined.expression, remap);
  const trimmed = expression.trim();
  // An empty expression is already flagged as incomplete elsewhere
  // (algorithmValidity) — leave it for the user to fill in rather than
  // inventing content for them.
  if (trimmed && !expressionTokens(expression).has(periodOperandId)) {
    expression = `(${expression}) AND ${periodOperandId}`;
  }
  return [...base, { ...topCombined, expression }];
}

/** Word tokens referenced in a combined expression (ids and operators alike —
 *  callers only ever probe for a specific id, never an operator). */
function expressionTokens(expression: string): Set<string> {
  return new Set(expression.match(/[A-Za-z0-9_]+/g) ?? []);
}

// The boolean keywords a combined expression's grammar accepts (see PLAIN_AND
// and the "OR/NOT/parentheses" customized branch of withAutoCombined above).
const EXPRESSION_KEYWORDS = new Set(['AND', 'OR', 'NOT']);

/**
 * Whole-word tokens in a combined expression that name neither a card in
 * `pool` nor a boolean keyword — a stale operand left behind when the filter
 * it pointed at was removed without updating the expression.
 *
 * `withAutoCombined`'s customized-combined branch deliberately leaves a
 * user-customized expression untouched when its period operand disappears
 * (dropping an operand means taking its surrounding operator with it, which
 * isn't safe to guess), so a card removal — `clearPeriodSelection`, unticking
 * the last period value, or an orphaned `periodOr` on a loaded config — can
 * leave an id like `m0periodOr` referenced here with nothing behind it. That
 * expression still ships to the wire as-is; this is what lets
 * `algorithmMissingFields` catch it before it does, rather than the Lib
 * silently evaluating an incoherent expression.
 */
export function unresolvedExpressionIds(expression: string, pool: FilterSpec[]): string[] {
  const ids = new Set(pool.map((f) => f.id));
  return [...expressionTokens(expression)].filter(
    (t) => !EXPRESSION_KEYWORDS.has(t) && !ids.has(t)
  );
}

/** Order-insensitive key for a spatial filter's solid set: two cards with the
 *  same solids (in any order) build the same volume. */
function spatialSolidsKey(solids: string[]): string {
  return [...solids].sort().join('\0');
}

/** Replace whole-token filter ids in a combined expression per `remap`. Ids are
 *  word-char only (enforced by the FilterEditor), so matching maximal word runs
 *  isolates operands cleanly and leaves operators (AND/OR/NOT) alone. */
function remapExpressionIds(expression: string, remap: Map<string, string>): string {
  return expression.replace(/[A-Za-z0-9_]+/g, (tok) => remap.get(tok) ?? tok);
}

/**
 * Collapse spatial filters that reference the same solid set into one. A spatial
 * filter is pure geometry (model-independent), so the common case — the same
 * solid picked on both models — yields two cards that would make the C++ wrapper
 * read and rasterize the same solid twice. Keep the first-seen spatial filter,
 * remap every reference to the duplicates (both `model_filters` slots and any
 * combined-selector expressions) onto it, and drop the redundant entries.
 *
 * Only `spatial` filters are merged: model-reading filters (categorical /
 * continuous) are model-specific and must never be deduped even if identical.
 * Empty-solids cards (an incomplete pick the editor blocks) are left as-is.
 */
export function dedupeSpatialSolids(
  filters: FilterSpec[],
  modelFilters: (string | null)[]
): { filters: FilterSpec[]; modelFilters: (string | null)[] } {
  const canonicalByKey = new Map<string, string>();
  const remap = new Map<string, string>(); // duplicate id -> canonical id
  for (const f of filters) {
    if (f.type !== 'spatial' || f.solids.length === 0) continue;
    const key = spatialSolidsKey(f.solids);
    const canonical = canonicalByKey.get(key);
    if (canonical === undefined) canonicalByKey.set(key, f.id);
    else remap.set(f.id, canonical);
  }
  if (remap.size === 0) return { filters, modelFilters };

  const nextFilters = filters
    .filter((f) => !remap.has(f.id))
    .map((f) =>
      f.type === 'combined' ? { ...f, expression: remapExpressionIds(f.expression, remap) } : f
    );
  const nextModelFilters = modelFilters.map((id) => (id == null ? id : (remap.get(id) ?? id)));
  return { filters: nextFilters, modelFilters: nextModelFilters };
}
